/**
 * Background cast analysis: short books POST `aistorycast-analyze-cast`; longer books
 * map/reduce via `aistorycast-analyze-cast-chunk`, merged client-side, persisted to IndexedDB.
 */
import { buildCastTextChunksForFullBook, estimateTotalBookChars } from "@/lib/castAnalysisChunkPlan";
import type { CastChunkAnalysisOk } from "@/lib/castAnalysisChunkPayload";
import { parseCastChunkWebhookPayload } from "@/lib/castAnalysisChunkPayload";
import { mergeCastChunkAnalyses } from "@/lib/castAnalysisMerge";
import { bookCastFromMergePayload, preliminaryCastForMergeApi } from "@/lib/castAnalysisMergeCanonical";
import { normalizeBookCastFromWebhook } from "@/lib/castAnalysisNormalize";
import type { BookCastAnalysisRow, BookCastJson, CastChunkFailureRecord } from "@/lib/castAnalysisTypes";
import {
  getCastAnalysisRow,
  getChapterText,
  getFullText,
  listImportedChapterSlugs,
  putCastAnalysisRow,
  sortChapterSlugs,
} from "@/lib/importedBookDb";

export const CAST_ANALYSIS_UPDATED_EVENT = "aistorycast-cast-analysis-updated";

const DEFAULT_ANALYZE_CAST_URL =
  "https://n8n.jdpenterprises.ai/webhook/aistorycast-analyze-cast";

const DEFAULT_ANALYZE_CAST_CHUNK_URL =
  "https://n8n.jdpenterprises.ai/webhook/aistorycast-analyze-cast-chunk";

const DEFAULT_MERGE_CAST_URL = "https://n8n.jdpenterprises.ai/webhook/aistorycast-merge-cast";

/** Aligns with server-side excerpt cap on the monolithic analyze-cast workflow (~28k). */
const EXCERPT_CAST_CHAR_THRESHOLD = 28_000;

const CLIENT_BOOK_TEXT_CAP = 200_000;

const inFlight = new Set<string>();

function analyzeCastWebhookUrl(): string {
  const u = (import.meta.env.VITE_AISTORYCAST_ANALYZE_CAST_WEBHOOK_URL as string | undefined)?.trim();
  return u && u.length > 0 ? u : DEFAULT_ANALYZE_CAST_URL;
}

function analyzeCastChunkWebhookUrl(): string {
  const u = (import.meta.env.VITE_AISTORYCAST_ANALYZE_CAST_CHUNK_WEBHOOK_URL as string | undefined)?.trim();
  return u && u.length > 0 ? u : DEFAULT_ANALYZE_CAST_CHUNK_URL;
}

function analyzeCastMergeWebhookUrl(): string {
  const u = (import.meta.env.VITE_AISTORYCAST_MERGE_CAST_WEBHOOK_URL as string | undefined)?.trim();
  return u && u.length > 0 ? u : DEFAULT_MERGE_CAST_URL;
}

function stripHtmlToSnippet(html: string, maxLen: number): string {
  const t = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return t.slice(0, maxLen);
}

function isLikelyBrowserNetworkBlock(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network request failed") ||
    m.includes("load failed")
  );
}

function explainCastFetchFailure(url: string, cause: unknown): string {
  const base = cause instanceof Error ? cause.message : String(cause);
  if (!isLikelyBrowserNetworkBlock(base)) return base;
  return (
    `${base} — Usually CORS preflight blocked or the webhook is missing on n8n. ` +
    `Confirm OPTIONS/POST for ${url} return access-control-allow-origin for https://aistorycast.com ` +
    `and publish the workflow "AIStoryCast Analyze Cast" (path aistorycast-analyze-cast), e.g. run: node scripts/push-n8n-analyze-cast-only.mjs`
  );
}

function explainCastChunkFetchFailure(url: string, cause: unknown): string {
  const base = cause instanceof Error ? cause.message : String(cause);
  if (!isLikelyBrowserNetworkBlock(base)) return base;
  return (
    `${base} — Usually CORS preflight blocked or the webhook is missing on n8n. ` +
    `Confirm OPTIONS/POST for ${url} return access-control-allow-origin for https://aistorycast.com ` +
    `and publish the workflow "AIStoryCast Analyze Cast Chunk" (path aistorycast-analyze-cast-chunk), e.g. run: node scripts/push-n8n-analyze-cast-chunk-only.mjs`
  );
}

function explainCastMergeFetchFailure(url: string, cause: unknown): string {
  const base = cause instanceof Error ? cause.message : String(cause);
  if (!isLikelyBrowserNetworkBlock(base)) return base;
  return (
    `${base} — Usually CORS preflight blocked or the webhook is missing on n8n. ` +
    `Publish "AIStoryCast Merge Cast Canonical" (path aistorycast-merge-cast), e.g. run: node scripts/push-n8n-merge-cast-only.mjs`
  );
}

export function dispatchCastAnalysisUpdated(bookId: string) {
  if (typeof window === "undefined") return;
  console.debug("[cast-analysis] dispatch updated event", { bookId });
  window.dispatchEvent(new CustomEvent(CAST_ANALYSIS_UPDATED_EVENT, { detail: { bookId } }));
}

function logCastAnalysisReadyRow(bookId: string, document: BookCastJson) {
  console.info("[cast-analysis] write ready row", {
    bookId,
    analysisStatus: "ready" as const,
    merge_pass_applied: document.merge_pass_applied === true,
    characterCount: Array.isArray(document.characters) ? document.characters.length : 0,
    analysis_mode: document.analysis_mode,
    success: document.success,
  });
}

async function loadBookTextForAnalysis(bookId: string): Promise<string | null> {
  const full = await getFullText(bookId);
  if (typeof full === "string" && full.trim().length > 0) {
    return full.trim().slice(0, CLIENT_BOOK_TEXT_CAP);
  }
  const slugs = sortChapterSlugs(await listImportedChapterSlugs(bookId));
  if (slugs.length === 0) return null;
  const parts: string[] = [];
  let total = 0;
  for (const slug of slugs) {
    const t = await getChapterText(bookId, slug);
    if (!t?.trim()) continue;
    const piece = t.trim();
    const add = piece.length + (parts.length ? 2 : 0);
    if (total + add > CLIENT_BOOK_TEXT_CAP) {
      const room = CLIENT_BOOK_TEXT_CAP - total - (parts.length ? 2 : 0);
      if (room > 80) parts.push(piece.slice(0, room));
      break;
    }
    parts.push(piece);
    total += add;
  }
  const joined = parts.join("\n\n");
  return joined.length > 0 ? joined : null;
}

function chunkOverrideForPlan(bookTextOverride?: string): string | undefined {
  if (typeof bookTextOverride !== "string" || !bookTextOverride.trim()) return undefined;
  return bookTextOverride.trim().slice(0, CLIENT_BOOK_TEXT_CAP);
}

/**
 * Queue cast analysis after a successful import (non-blocking). Idempotent while `processing`
 * unless `force` is true (re-import / retry).
 */
export function scheduleCastAnalysisAfterImport(args: {
  bookId: string;
  title: string;
  author: string;
  source?: string;
  /** Fresh text from import path (avoids waiting on IDB read coherency). */
  bookTextOverride?: string;
  force?: boolean;
}): void {
  void runCastAnalysisJob(args);
}

async function runCastAnalysisJob(args: {
  bookId: string;
  title: string;
  author: string;
  source?: string;
  bookTextOverride?: string;
  force?: boolean;
}): Promise<void> {
  const { bookId, title, author, source, bookTextOverride, force } = args;
  if (typeof window === "undefined") return;

  if (inFlight.has(bookId)) return;
  const existing = await getCastAnalysisRow(bookId);
  if (!force && existing?.analysisStatus === "ready") {
    return;
  }

  inFlight.add(bookId);
  const now = () => new Date().toISOString();

  try {
    await putCastAnalysisRow({
      bookId,
      analysisStatus: "queued",
      source,
      updatedAt: now(),
      errorMessage: undefined,
      document: null,
    });
    dispatchCastAnalysisUpdated(bookId);

    await putCastAnalysisRow({
      bookId,
      analysisStatus: "processing",
      source,
      updatedAt: now(),
      errorMessage: undefined,
      document: null,
    });
    dispatchCastAnalysisUpdated(bookId);

    const totalChars = await estimateTotalBookChars(bookId, bookTextOverride ?? null);
    const useExcerptPipeline = totalChars <= EXCERPT_CAST_CHAR_THRESHOLD;

    if (useExcerptPipeline) {
      const bookText =
        typeof bookTextOverride === "string" && bookTextOverride.trim().length > 0
          ? bookTextOverride.trim().slice(0, CLIENT_BOOK_TEXT_CAP)
          : await loadBookTextForAnalysis(bookId);

      if (!bookText) {
        const err = "No book text found for cast analysis.";
        await putCastAnalysisRow({
          bookId,
          analysisStatus: "error",
          source,
          updatedAt: now(),
          errorMessage: err,
          document: normalizeBookCastFromWebhook({ success: false, error: err }, { bookId, title, author, source }),
        });
        dispatchCastAnalysisUpdated(bookId);
        return;
      }

      const url = analyzeCastWebhookUrl();
      let response: Response;
      try {
        response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            book_id: bookId,
            title,
            author,
            book_text: bookText,
            source: source ?? "import",
          }),
        });
      } catch (netErr) {
        const err = explainCastFetchFailure(url, netErr);
        await putCastAnalysisRow({
          bookId,
          analysisStatus: "error",
          source,
          updatedAt: now(),
          errorMessage: err,
          document: normalizeBookCastFromWebhook({ success: false, error: err }, { bookId, title, author, source }),
        });
        dispatchCastAnalysisUpdated(bookId);
        return;
      }

      const raw = await response.text();
      let payload: unknown;
      try {
        payload = raw ? JSON.parse(raw) : {};
      } catch {
        const err = "Invalid JSON from cast analysis service.";
        await putCastAnalysisRow({
          bookId,
          analysisStatus: "error",
          source,
          updatedAt: now(),
          errorMessage: err,
          document: normalizeBookCastFromWebhook({ success: false, error: err }, { bookId, title, author, source }),
        });
        dispatchCastAnalysisUpdated(bookId);
        return;
      }

      if (!response.ok) {
        const jsonErr =
          typeof payload === "object" &&
          payload !== null &&
          "error" in payload &&
          typeof (payload as { error?: unknown }).error === "string"
            ? (payload as { error: string }).error
            : null;
        const htmlHint = raw.trim().startsWith("<") ? stripHtmlToSnippet(raw, 220) : raw.trim().slice(0, 280);
        const hint = jsonErr ?? (htmlHint || "(empty body)");
        const deployHint =
          response.status === 404 || response.status === 502 || raw.includes("<!DOCTYPE")
            ? ` If the webhook is not registered, run: node scripts/push-n8n-analyze-cast-only.mjs`
            : "";
        const err = `HTTP ${response.status}: ${hint}${deployHint}`;
        await putCastAnalysisRow({
          bookId,
          analysisStatus: "error",
          source,
          updatedAt: now(),
          errorMessage: err,
          document: normalizeBookCastFromWebhook({ success: false, error: err }, { bookId, title, author, source }),
        });
        dispatchCastAnalysisUpdated(bookId);
        return;
      }

      const doc = normalizeBookCastFromWebhook(payload, { bookId, title, author, source });
      if (!doc.success || doc.status === "error") {
        const err = doc.error ?? "Cast analysis failed.";
        const row: BookCastAnalysisRow = {
          bookId,
          analysisStatus: "error",
          source,
          updatedAt: now(),
          errorMessage: err,
          document: doc,
        };
        await putCastAnalysisRow(row);
        dispatchCastAnalysisUpdated(bookId);
        return;
      }

      await putCastAnalysisRow({
        bookId,
        analysisStatus: "ready",
        source,
        updatedAt: now(),
        errorMessage: undefined,
        document: doc,
      });
      logCastAnalysisReadyRow(bookId, doc);
      dispatchCastAnalysisUpdated(bookId);
      return;
    }

    const textChunks = await buildCastTextChunksForFullBook(bookId, chunkOverrideForPlan(bookTextOverride));
    if (textChunks.length === 0) {
      const err = "No book text found for cast analysis.";
      await putCastAnalysisRow({
        bookId,
        analysisStatus: "error",
        source,
        updatedAt: now(),
        errorMessage: err,
        document: normalizeBookCastFromWebhook({ success: false, error: err }, { bookId, title, author, source }),
      });
      dispatchCastAnalysisUpdated(bookId);
      return;
    }

    const chunkUrl = analyzeCastChunkWebhookUrl();
    const okChunks: CastChunkAnalysisOk[] = [];
    const failedChunks: CastChunkFailureRecord[] = [];
    const n = textChunks.length;

    for (let i = 0; i < n; i++) {
      const ch = textChunks[i]!;
      await putCastAnalysisRow({
        bookId,
        analysisStatus: "processing",
        source,
        updatedAt: now(),
        errorMessage: undefined,
        document: null,
        progress: { analysisMode: "full", chunksTotal: n, chunksReady: i },
      });
      dispatchCastAnalysisUpdated(bookId);

      const pushFailure = (error: string, raw_preview?: string) => {
        failedChunks.push({
          chunk_index: ch.index,
          chunk_id: ch.chunkId,
          error,
          raw_preview: raw_preview?.slice(0, 1000),
        });
        console.warn("[cast-analysis] chunk failed (continuing)", {
          bookId,
          chunk_index: ch.index,
          chunk_id: ch.chunkId,
          error,
        });
      };

      let response: Response;
      try {
        response = await fetch(chunkUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            book_id: bookId,
            title,
            author,
            chunk_id: ch.chunkId,
            chunk_text: ch.text,
            chunk_index: ch.index,
            chunks_total: n,
            source: source ?? "import",
          }),
        });
      } catch (netErr) {
        pushFailure(explainCastChunkFetchFailure(chunkUrl, netErr));
        continue;
      }

      const raw = await response.text();
      let payload: unknown;
      try {
        payload = raw ? JSON.parse(raw) : {};
      } catch {
        pushFailure("Invalid JSON from cast chunk analysis service (non-JSON HTTP body).", raw);
        continue;
      }

      if (!response.ok) {
        const jsonErr =
          typeof payload === "object" &&
          payload !== null &&
          "error" in payload &&
          typeof (payload as { error?: unknown }).error === "string"
            ? (payload as { error: string }).error
            : null;
        const htmlHint = raw.trim().startsWith("<") ? stripHtmlToSnippet(raw, 220) : raw.trim().slice(0, 280);
        const hint = jsonErr ?? (htmlHint || "(empty body)");
        const deployHint =
          response.status === 404 || response.status === 502 || raw.includes("<!DOCTYPE")
            ? ` If the webhook is not registered, run: node scripts/push-n8n-analyze-cast-chunk-only.mjs`
            : "";
        pushFailure(`Cast chunk ${i + 1}/${n} (HTTP ${response.status}): ${hint}${deployHint}`, raw);
        continue;
      }

      const parsed = parseCastChunkWebhookPayload(payload);
      if (parsed.success === false) {
        pushFailure(`Cast chunk ${i + 1}/${n}: ${parsed.error}`, parsed.raw_preview ?? raw);
        continue;
      }
      if (parsed.book_id !== bookId) {
        pushFailure(`Cast chunk ${i + 1}/${n}: book_id mismatch (expected ${bookId}, got ${parsed.book_id}).`);
        continue;
      }

      okChunks.push(parsed);
    }

    if (okChunks.length === 0) {
      const summary =
        failedChunks.length > 0
          ? `All ${n} cast chunk(s) failed. First error: ${failedChunks[0]?.error ?? "unknown"}.`
          : "No cast chunk results to merge.";
      await putCastAnalysisRow({
        bookId,
        analysisStatus: "error",
        source,
        updatedAt: now(),
        errorMessage: summary,
        document: normalizeBookCastFromWebhook({ success: false, error: summary }, { bookId, title, author, source }),
      });
      dispatchCastAnalysisUpdated(bookId);
      return;
    }

    const merged = mergeCastChunkAnalyses({ bookId, title, author, source }, okChunks);
    const mergeUrl = analyzeCastMergeWebhookUrl();
    let finalDoc = merged;
    try {
      const mergeBody = {
        book_id: bookId,
        title,
        author,
        chunks_analyzed: merged.chunks_analyzed ?? okChunks.length,
        preliminary_cast: preliminaryCastForMergeApi(merged),
        source: source ?? "import",
      };
      const mergeRes = await fetch(mergeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mergeBody),
      });
      const mergeRaw = await mergeRes.text();
      let mergePayload: unknown = {};
      try {
        mergePayload = mergeRaw ? JSON.parse(mergeRaw) : {};
      } catch {
        mergePayload = { success: false, error: "Invalid JSON from cast merge service.", stage: "parse" };
      }
      const pipelinePayload = mergeRes.ok
        ? mergePayload
        : { success: false, error: `HTTP ${mergeRes.status}`, stage: "http" };
      finalDoc = bookCastFromMergePayload(merged, pipelinePayload, { source });
    } catch (mergeNet) {
      finalDoc = {
        ...merged,
        merge_pass_applied: false,
        cast_merge_debug: {
          preliminary_count: merged.characters.length,
          preliminary_names: merged.characters.map((c) => c.name).slice(0, 200),
          merge_error: explainCastMergeFetchFailure(mergeUrl, mergeNet),
        },
      };
    }

    if (failedChunks.length > 0) {
      const warn = `${failedChunks.length} of ${n} segment(s) failed; results may be incomplete.`;
      finalDoc = {
        ...finalDoc,
        chunks_analyzed: n,
        cast_analysis_warnings: [...(finalDoc.cast_analysis_warnings ?? []), warn],
        failed_chunks: failedChunks,
      };
    }

    await putCastAnalysisRow({
      bookId,
      analysisStatus: "ready",
      source,
      updatedAt: now(),
      errorMessage: undefined,
      document: finalDoc,
    });
    logCastAnalysisReadyRow(bookId, finalDoc);
    dispatchCastAnalysisUpdated(bookId);
  } catch (e) {
    console.error("[cast-analysis] job failed", { bookId, error: e });
    const err = explainCastFetchFailure(analyzeCastWebhookUrl(), e);
    await putCastAnalysisRow({
      bookId,
      analysisStatus: "error",
      source,
      updatedAt: now(),
      errorMessage: err,
      document: normalizeBookCastFromWebhook({ success: false, error: err }, { bookId, title, author, source }),
    });
    dispatchCastAnalysisUpdated(bookId);
  } finally {
    inFlight.delete(bookId);
  }
}

/** User-triggered retry from reader UI. */
export function retryCastAnalysis(args: {
  bookId: string;
  title: string;
  author: string;
  source?: string;
}): void {
  void runCastAnalysisJob({ ...args, force: true });
}
